import { useAppDispatch, useAppSelector } from '@/shared/lib/redux';
import { PieChart } from '@/shared/ui/pie-chart';
import { useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { closeInspectStoreModal, selectCurrentStore, selectEndDate, selectInspectStoreModalOpen, selectStartDate, selectStoreStatistics, setEndDate, setStartDate } from '../../model';
import { selectBonuses } from '@/entities/bonus';
import AvatarImg from '@/shared/assets/images/AvatarOem.png'
import DatePicker from 'react-datepicker';
import CalendarIcon from '@/shared/assets/images/CalendarBlank.svg';

const styles: ReactModal.Styles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'transparent',
        border: 'none'
    },
    overlay: {
        backgroundColor: 'rgba(22, 47, 43, 0.6)',
    }
}

export const InspectStoreModal = () => {
    const bonuses = useAppSelector(selectBonuses)
    const aggregates = useAppSelector(selectStoreStatistics)
    const selectedStore = useAppSelector(selectCurrentStore)
    const isOpen = useAppSelector(selectInspectStoreModalOpen)
    const startDate = useAppSelector(selectStartDate)
    const endDate = useAppSelector(selectEndDate)
    const dispatch = useAppDispatch()
    const [draftPeriod, setDraftPeriod] = useState<[Date | null, Date | null]>([startDate, endDate])
    const isRangePending = !draftPeriod[0] || !draftPeriod[1]

    useEffect(() => {
        if (isOpen) setDraftPeriod([startDate, endDate])
    }, [isOpen])

    const handleClose = () => {
        dispatch(closeInspectStoreModal())
    }

    const handleDateRangeChange = ([start, end]: [Date | null, Date | null]) => {
        setDraftPeriod([start, end])
        // Keep the last valid period in Redux while the second date is pending.
        if (start && end) {
            dispatch(setStartDate(start))
            dispatch(setEndDate(end))
        }
    }

    const accruedData = useMemo(() => {
        const parsed = bonuses
            .map((bonus) => ({
                bonus: bonus,
                data: aggregates[selectedStore?.id]?.bonuses[bonus.id]?.totalAccrued
            }))
            .filter((exists) => exists.data ?? 0)
        const labels = parsed.map(obj => obj.bonus.name)
        const data = parsed.map(obj => obj.data)
        return {
            labels,
            datasets: [{
                data
            }]
        }

    }, [bonuses, aggregates, selectedStore])

    const spentData = useMemo(() => {
        const parsed = bonuses
            .map((bonus) => ({
                bonus: bonus,
                data: aggregates[selectedStore?.id]?.bonuses[bonus.id]?.totalSpent
            }))
            .filter((exists) => exists.data ?? 0)
        const labels = parsed.map(obj => obj.bonus.name)
        const data = parsed.map(obj => obj.data)
        return {
            labels,
            datasets: [{
                data
            }]
        }

    }, [bonuses, aggregates, selectedStore])

    return (
        <ReactModal
            isOpen={isOpen}
            style={styles}
            onRequestClose={handleClose}
        >
            <div className='flex gap-4 flex-col min-w-[70vw]'>
                <div className='flex flex-col p-6 bg-white rounded-3xl'>
                    <div className='flex justify-between w-full items-center min-w-[548px]'>
                        <div className='font-medium font-mediumttf text-2xl'>
                            Статистика торговой точки
                        </div>
                        <button onClick={handleClose} className='w-[16.5px] h-[16.5px] flex items-center justify-center relative'>
                            <div className='h-[23px] absolute w-[2px] block bg-color-dark rounded-lg rotate-45' />
                            <div className='h-[23px] absolute w-[2px] block bg-color-dark rounded-lg -rotate-45' />
                        </button>
                    </div>
                    <div className='bg-[#E7EAEE] my-3 w-full h-[1px] block' />
                    <div className='mb-4 flex flex-wrap items-center gap-3'>
                        <label htmlFor='store-statistics-period' className='font-medium'>Период статистики</label>
                        <DatePicker
                            id='store-statistics-period'
                            selected={draftPeriod[0]}
                            startDate={draftPeriod[0]}
                            endDate={draftPeriod[1]}
                            selectsRange
                            onChange={handleDateRangeChange}
                            wrapperClassName='react-datepicker-filterlike'
                            dateFormat='dd.MM.yyyy'
                            showIcon
                            popperProps={{ placement: 'bottom' }}
                            icon={<CalendarIcon width={24} height={24} />}
                        />
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='w-[60px] h-[60px] rounded-xl bg-color-dark'>
                            <img src={AvatarImg} className='h-full w-full object-contain' />
                        </div>
                        <div>
                            <div className='font-semibold uppercase text-xl'>
                                {selectedStore?.name || 'Торговая точка ' + selectedStore?.partnerName}
                            </div>
                        </div>
                    </div>
                </div>
                {isRangePending
                    ? <div className="box !w-full !rounded-3xl">
                        <div className="flex min-h-[284px] items-center justify-center px-6 py-10 text-center text-slate-500">
                            Выберите начало и окончание периода, чтобы показать статистику.
                        </div>
                    </div>
                    : <div className="flex gap-4">
                        <div className='box !w-full !h-min !rounded-3xl'>
                            <div className='box_header'>
                                Принятые к оплате бонусы
                            </div>
                            <div className='bg-[#E7EAEE] mb-6 screen:mb-[32px] w-full h-[1px] block' />
                            <div id='chart' className='min-h-[284px] flex h-full'>
                                {Boolean(spentData.labels.length)
                                    ? <PieChart data={spentData} />
                                    : <div className='flex flex-1 mb-[32px] items-center'>
                                        <div className='w-full rounded-3xl text-center py-5 uppercase bg-color-dark text-white'>
                                            нет данных
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className='box !w-full !h-min !rounded-3xl'>
                            <div className='box_header'>
                                Начисленные бонусы
                            </div>
                            <div className='bg-[#E7EAEE] mb-6 screen:mb-[32px] w-full h-[1px] block' />
                            <div id='chart' className='min-h-[284px] flex h-full'>
                                {Boolean(accruedData.labels.length)
                                    ? <PieChart data={accruedData} />
                                    : <div className='flex flex-1 mb-[32px] items-center'>
                                        <div className='w-full rounded-3xl text-center py-5 uppercase bg-color-dark text-white'>
                                            нет данных
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
        </ReactModal>
    )
}